/**
 * AudioPlayer
 *
 * Plays base64-encoded 24kHz PCM16 audio chunks with gapless scheduling.
 * Exposes a `level` property (0–1) representing current playback volume
 * via an AnalyserNode.
 */

const PLAYBACK_SAMPLE_RATE = 24000;

export class AudioPlayer {
    private audioContext: AudioContext | null = null;
    private gainNode: GainNode | null = null;
    private analyser: AnalyserNode | null = null;
    private analyserData: Uint8Array | null = null;
    private nextStartTime = 0;
    private isPlaying = false;

    /** Current playback level (0–1), call getLevel() to read. */
    level = 0;

    start(): void {
        if (!this.audioContext) {
            this.audioContext = new AudioContext({ sampleRate: PLAYBACK_SAMPLE_RATE });

            // Central gain node → destination + analyser
            this.gainNode = this.audioContext.createGain();
            this.gainNode.connect(this.audioContext.destination);

            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            this.gainNode.connect(this.analyser);
            this.analyserData = new Uint8Array(this.analyser.frequencyBinCount);
        }
        this.nextStartTime = 0;
        this.isPlaying = true;
    }

    /**
     * Enqueue a base64-encoded PCM16 chunk for playback.
     */
    playChunk(base64Data: string): void {
        if (!this.audioContext || !this.isPlaying || !this.gainNode) return;

        const pcmBytes = base64ToArrayBuffer(base64Data);
        const pcm16 = new Int16Array(pcmBytes);
        const float32 = pcm16ToFloat32(pcm16);

        const buffer = this.audioContext.createBuffer(1, float32.length, PLAYBACK_SAMPLE_RATE);
        buffer.getChannelData(0).set(float32);

        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.gainNode);

        const currentTime = this.audioContext.currentTime;
        const startTime = Math.max(currentTime, this.nextStartTime);
        source.start(startTime);
        this.nextStartTime = startTime + buffer.duration;
    }

    /**
     * Read current playback level from the analyser (0–1).
     */
    getLevel(): number {
        if (!this.analyser || !this.analyserData) return 0;
        this.analyser.getByteTimeDomainData(this.analyserData);

        let sum = 0;
        for (let i = 0; i < this.analyserData.length; i++) {
            const sample = (this.analyserData[i] - 128) / 128;
            sum += sample * sample;
        }
        return Math.min(1, Math.sqrt(sum / this.analyserData.length) * 4);
    }

    stop(): void {
        this.isPlaying = false;
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.gainNode = null;
        this.analyser = null;
        this.analyserData = null;
        this.nextStartTime = 0;
        this.level = 0;
    }
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

function pcm16ToFloat32(pcm16: Int16Array): Float32Array {
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7FFF);
    }
    return float32;
}
